import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

// In-memory cache to reduce Firebase requests
const habitCache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

// Cache helper functions
const getCacheKey = (fn, userId, ...args) => `${fn}_${userId}_${args.join('_')}`;

const getFromCache = (key) => {
  const cached = habitCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  habitCache.delete(key);
  return null;
};

const setCache = (key, data) => {
  habitCache.set(key, { data, timestamp: Date.now() });
};

// ===============================
// GLOBAL HABIT CONFIGURATION
// ===============================

// Initialize all app configurations (icons, units, categories, etc.)
export const initializeAppConfigurations = async () => {
  try {
    console.log('🔄 Initializing normalized app configurations in Firebase...');
    console.log('📍 Target location: /apps/habit-tracker/config/');
    
    // Load normalized data from JSON files (this would be the actual JSON data)
    const normalizedData = {
      icons: {
        "water-drop": { "id": "water-drop", "emoji": "💧", "name": "Water Drop", "category": "health", "tags": ["water", "hydration", "liquid", "drink"] },
        "running": { "id": "running", "emoji": "🏃", "name": "Running", "category": "health", "tags": ["exercise", "cardio", "fitness", "sport"] },
        "walking": { "id": "walking", "emoji": "🚶", "name": "Walking", "category": "health", "tags": ["exercise", "steps", "movement", "cardio"] },
        "sleep": { "id": "sleep", "emoji": "💤", "name": "Sleep", "category": "health", "tags": ["rest", "recovery", "bedtime", "zzz"] },
        "apple": { "id": "apple", "emoji": "🍎", "name": "Apple", "category": "health", "tags": ["fruit", "nutrition", "healthy", "food"] },
        "exercise": { "id": "exercise", "emoji": "🏋️", "name": "Exercise", "category": "health", "tags": ["workout", "fitness", "strength", "gym"] },
        "tooth": { "id": "tooth", "emoji": "🦷", "name": "Tooth", "category": "personal", "tags": ["dental", "hygiene", "brush", "care"] },
        "pill": { "id": "pill", "emoji": "💊", "name": "Pill", "category": "health", "tags": ["medicine", "supplement", "vitamin", "health"] },
        "meditation": { "id": "meditation", "emoji": "🧘", "name": "Meditation", "category": "mindfulness", "tags": ["zen", "calm", "peace", "mindful"] },
        "prayer": { "id": "prayer", "emoji": "🙏", "name": "Prayer", "category": "mindfulness", "tags": ["gratitude", "thanks", "spiritual", "blessing"] },
        "breathing": { "id": "breathing", "emoji": "🌬️", "name": "Breathing", "category": "mindfulness", "tags": ["breath", "air", "wind", "calm"] },
        "target": { "id": "target", "emoji": "🎯", "name": "Target", "category": "productivity", "tags": ["goal", "aim", "focus", "objective"] },
        "lightning": { "id": "lightning", "emoji": "⚡", "name": "Lightning", "category": "productivity", "tags": ["energy", "power", "fast", "quick"] },
        "folder": { "id": "folder", "emoji": "🗂️", "name": "Folder", "category": "productivity", "tags": ["organize", "files", "structure", "system"] },
        "book": { "id": "book", "emoji": "📚", "name": "Book", "category": "learning", "tags": ["read", "study", "knowledge", "education"] },
        "writing": { "id": "writing", "emoji": "📝", "name": "Writing", "category": "learning", "tags": ["note", "write", "text", "document"] },
        "graduation": { "id": "graduation", "emoji": "🎓", "name": "Graduation", "category": "learning", "tags": ["education", "degree", "achievement", "school"] },
        "pen": { "id": "pen", "emoji": "✍️", "name": "Pen", "category": "learning", "tags": ["write", "journal", "note", "handwriting"] },
        "music": { "id": "music", "emoji": "🎵", "name": "Music", "category": "creative", "tags": ["note", "song", "melody", "sound"] },
        "art": { "id": "art", "emoji": "🎨", "name": "Art", "category": "creative", "tags": ["paint", "creative", "artist", "design"] },
        "call": { "id": "call", "emoji": "📞", "name": "Phone Call", "category": "social", "tags": ["communication", "talk", "contact", "voice"] },
        "message": { "id": "message", "emoji": "💌", "name": "Message", "category": "social", "tags": ["communication", "text", "letter", "contact"] },
        "skincare": { "id": "skincare", "emoji": "🧴", "name": "Skincare", "category": "personal", "tags": ["beauty", "care", "routine", "hygiene"] },
        "checkmark": { "id": "checkmark", "emoji": "✅", "name": "Checkmark", "category": "general", "tags": ["done", "complete", "success", "check"] },
        "target-general": { "id": "target-general", "emoji": "🎯", "name": "Target", "category": "general", "tags": ["goal", "aim", "focus", "objective"] },
        "star": { "id": "star", "emoji": "🌟", "name": "Star", "category": "general", "tags": ["achievement", "success", "bright", "special"] }
      },

      units: {
        "minutes": { "id": "minutes", "name": "minutes", "singular": "minute", "category": "time", "abbreviation": "min" },
        "hours": { "id": "hours", "name": "hours", "singular": "hour", "category": "time", "abbreviation": "hr" },
        "seconds": { "id": "seconds", "name": "seconds", "singular": "second", "category": "time", "abbreviation": "sec" },
        "times": { "id": "times", "name": "times", "singular": "time", "category": "quantity", "abbreviation": "x" },
        "reps": { "id": "reps", "name": "reps", "singular": "rep", "category": "quantity", "abbreviation": "reps" },
        "sets": { "id": "sets", "name": "sets", "singular": "set", "category": "quantity", "abbreviation": "sets" },
        "sessions": { "id": "sessions", "name": "sessions", "singular": "session", "category": "quantity", "abbreviation": "sessions" },
        "glasses": { "id": "glasses", "name": "glasses", "singular": "glass", "category": "volume", "abbreviation": "glasses" },
        "cups": { "id": "cups", "name": "cups", "singular": "cup", "category": "volume", "abbreviation": "cups" },
        "liters": { "id": "liters", "name": "liters", "singular": "liter", "category": "volume", "abbreviation": "L" },
        "bottles": { "id": "bottles", "name": "bottles", "singular": "bottle", "category": "volume", "abbreviation": "bottles" },
        "steps": { "id": "steps", "name": "steps", "singular": "step", "category": "distance", "abbreviation": "steps" },
        "kilometers": { "id": "kilometers", "name": "kilometers", "singular": "kilometer", "category": "distance", "abbreviation": "km" },
        "miles": { "id": "miles", "name": "miles", "singular": "mile", "category": "distance", "abbreviation": "mi" },
        "servings": { "id": "servings", "name": "servings", "singular": "serving", "category": "food", "abbreviation": "servings" },
        "pieces": { "id": "pieces", "name": "pieces", "singular": "piece", "category": "food", "abbreviation": "pcs" },
        "calories": { "id": "calories", "name": "calories", "singular": "calorie", "category": "food", "abbreviation": "cal" },
        "pages": { "id": "pages", "name": "pages", "singular": "page", "category": "learning", "abbreviation": "pp" },
        "chapters": { "id": "chapters", "name": "chapters", "singular": "chapter", "category": "learning", "abbreviation": "ch" },
        "words": { "id": "words", "name": "words", "singular": "word", "category": "learning", "abbreviation": "words" },
        "calls": { "id": "calls", "name": "calls", "singular": "call", "category": "social", "abbreviation": "calls" },
        "messages": { "id": "messages", "name": "messages", "singular": "message", "category": "social", "abbreviation": "msgs" },
        "doses": { "id": "doses", "name": "doses", "singular": "dose", "category": "health", "abbreviation": "doses" },
        "things": { "id": "things", "name": "things", "singular": "thing", "category": "general", "abbreviation": "things" },
        "routines": { "id": "routines", "name": "routines", "singular": "routine", "category": "general", "abbreviation": "routines" }
      },

      categories: [
        { "key": "health", "label": "Health & Fitness", "iconId": "exercise", "colorId": "green", "description": "Physical health, exercise, nutrition, and wellness habits" },
        { "key": "mindfulness", "label": "Mindfulness", "iconId": "meditation", "colorId": "purple", "description": "Mental health, meditation, gratitude, and spiritual practices" },
        { "key": "productivity", "label": "Productivity", "iconId": "lightning", "colorId": "blue", "description": "Work efficiency, goal setting, and task management" },
        { "key": "learning", "label": "Learning", "iconId": "book", "colorId": "gold", "description": "Education, skill development, and knowledge acquisition" },
        { "key": "social", "label": "Social", "iconId": "call", "colorId": "pink", "description": "Relationships, communication, and social connections" },
        { "key": "creative", "label": "Creative", "iconId": "art", "colorId": "cyan", "description": "Artistic expression, music, writing, and creative pursuits" },
        { "key": "personal", "label": "Personal Care", "iconId": "skincare", "colorId": "orange", "description": "Self-care, hygiene, grooming, and personal maintenance" },
        { "key": "other", "label": "Other", "iconId": "star", "colorId": "gray", "description": "Miscellaneous habits that don't fit other categories" }
      ],

      frequencies: [
        { "id": "daily", "value": "daily", "label": "Every Day", "description": "Habit should be performed every single day" },
        { "id": "weekdays", "value": "weekdays", "label": "Weekdays Only", "description": "Habit performed Monday through Friday only" },
        { "id": "weekends", "value": "weekends", "label": "Weekends Only", "description": "Habit performed Saturday and Sunday only" },
        { "id": "weekly", "value": "weekly", "label": "Weekly", "description": "Habit performed once per week" },
        { "id": "custom", "value": "custom", "label": "Custom Days", "description": "Habit performed on specific custom days (future feature)" }
      ],

      colors: [
        { "id": "blue", "name": "Blue", "value": "#1E90FF", "category": "primary" },
        { "id": "green", "name": "Green", "value": "#52c41a", "category": "primary" },
        { "id": "orange", "name": "Orange", "value": "#FFA500", "category": "warm" },
        { "id": "purple", "name": "Purple", "value": "#722ed1", "category": "cool" },
        { "id": "pink", "name": "Pink", "value": "#eb2f96", "category": "warm" },
        { "id": "cyan", "name": "Cyan", "value": "#13c2c2", "category": "cool" },
        { "id": "gold", "name": "Gold", "value": "#faad14", "category": "warm" },
        { "id": "red", "name": "Red", "value": "#f5222d", "category": "warm" },
        { "id": "teal", "name": "Teal", "value": "#20b2aa", "category": "cool" },
        { "id": "indigo", "name": "Indigo", "value": "#6610f2", "category": "cool" },
        { "id": "yellow", "name": "Yellow", "value": "#fadb14", "category": "bright" },
        { "id": "gray", "name": "Gray", "value": "#8c8c8c", "category": "neutral" }
      ]
    };
    
    // Upload each collection to Firebase
    console.log('📤 Uploading collections to Firebase...');
    
    await Promise.all([
      setDoc(doc(db, 'apps', 'habit-tracker', 'config', 'icons'), { data: normalizedData.icons }),
      setDoc(doc(db, 'apps', 'habit-tracker', 'config', 'units'), { data: normalizedData.units }),
      setDoc(doc(db, 'apps', 'habit-tracker', 'config', 'categories'), { data: normalizedData.categories }),
      setDoc(doc(db, 'apps', 'habit-tracker', 'config', 'frequencies'), { data: normalizedData.frequencies }),
      setDoc(doc(db, 'apps', 'habit-tracker', 'config', 'colors'), { data: normalizedData.colors })
    ]);
    
    console.log('✅ All normalized collections uploaded to Firebase');
    console.log('📝 Configuration summary:');
    console.log(`   🎨 Icons: ${Object.keys(normalizedData.icons).length} icons`);
    console.log(`   📏 Units: ${Object.keys(normalizedData.units).length} units`);
    console.log(`   📂 Categories: ${normalizedData.categories.length} categories`);
    console.log(`   ⏰ Frequencies: ${normalizedData.frequencies.length} frequencies`);
    console.log(`   🌈 Colors: ${normalizedData.colors.length} colors`);
    
    return true;
  } catch (error) {
    console.error('❌ Error initializing normalized configurations:', error);
    throw error;
  }
};

// Get app configuration by type
export const getAppConfiguration = async (configType) => {
  const cacheKey = `getAppConfig_${configType}`;
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log(`📋 Using cached ${configType} config`);
    return cached;
  }

  try {
    const configRef = doc(db, 'apps', 'habit-tracker', 'config', configType);
    const configDoc = await getDoc(configRef);
    
    if (configDoc.exists()) {
      const configData = configDoc.data().data;
      setCache(cacheKey, configData);
      console.log(`✅ Loaded ${configType} configuration from Firebase`);
      return configData;
    } else {
      console.log(`⚠️ No ${configType} configuration found in Firebase. Please run initialization.`);
      return Array.isArray(configType) ? [] : {};
    }
  } catch (error) {
    console.error(`❌ Error loading ${configType} configuration:`, error);
    return Array.isArray(configType) ? [] : {};
  }
};

// ===============================
// GLOBAL HABIT TEMPLATES
// ===============================

// Initialize global templates in Firebase (call this once)
export const initializeGlobalTemplates = async () => {
  try {
    console.log('🔄 Initializing global habit templates...');
    
    const templatesRef = collection(db, 'apps', 'habit-tracker', 'templates');
    
    // FIRST: Check what templates already exist to prevent duplicates
    console.log('🔍 Checking for existing templates to prevent duplicates...');
    const existingTemplatesSnapshot = await getDocs(templatesRef);
    const existingTemplateNames = new Set();
    
    existingTemplatesSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.name) {
        existingTemplateNames.add(data.name.toLowerCase().trim());
      }
    });
    
    console.log(`📋 Found ${existingTemplateNames.size} existing template names`);
    
    // NORMALIZED TEMPLATES - Using validated ID-based structure
    const habitTemplates = [
      // Health & Fitness
      { name: 'Drink 8 glasses of water', iconId: 'water-drop', category: 'health', colorId: 'blue', frequency: 'daily', targetCount: 8, unitId: 'glasses', isQuantifiable: true },
      { name: 'Exercise for 30 minutes', iconId: 'exercise', category: 'health', colorId: 'green', frequency: 'daily', targetCount: 30, unitId: 'minutes', isQuantifiable: true },
      { name: 'Take 10,000 steps', iconId: 'walking', category: 'health', colorId: 'gold', frequency: 'daily', targetCount: 10000, unitId: 'steps', isQuantifiable: true },
      { name: 'Sleep 8 hours', iconId: 'sleep', category: 'health', colorId: 'purple', frequency: 'daily', targetCount: 8, unitId: 'hours', isQuantifiable: true },
      { name: 'Eat 5 servings of fruits/vegetables', iconId: 'apple', category: 'health', colorId: 'green', frequency: 'daily', targetCount: 5, unitId: 'servings', isQuantifiable: true },
      
      // Mindfulness
      { name: 'Meditate', iconId: 'meditation', category: 'mindfulness', colorId: 'purple', frequency: 'daily', targetCount: 10, unitId: 'minutes', isQuantifiable: true },
      { name: 'Practice gratitude', iconId: 'prayer', category: 'mindfulness', colorId: 'gold', frequency: 'daily', targetCount: 3, unitId: 'things', isQuantifiable: true },
      { name: 'Deep breathing exercises', iconId: 'breathing', category: 'mindfulness', colorId: 'cyan', frequency: 'daily', targetCount: 5, unitId: 'minutes', isQuantifiable: true },
      
      // Productivity
      { name: 'Review daily goals', iconId: 'target', category: 'productivity', colorId: 'blue', frequency: 'daily', targetCount: 1, unitId: 'sessions', isQuantifiable: false },
      { name: 'Focus work session', iconId: 'lightning', category: 'productivity', colorId: 'orange', frequency: 'daily', targetCount: 2, unitId: 'sessions', isQuantifiable: true },
      { name: 'Organize workspace', iconId: 'folder', category: 'productivity', colorId: 'green', frequency: 'daily', targetCount: 1, unitId: 'times', isQuantifiable: false },
      
      // Learning
      { name: 'Read for 30 minutes', iconId: 'book', category: 'learning', colorId: 'gold', frequency: 'daily', targetCount: 30, unitId: 'minutes', isQuantifiable: true },
      { name: 'Learn new vocabulary', iconId: 'writing', category: 'learning', colorId: 'blue', frequency: 'daily', targetCount: 5, unitId: 'words', isQuantifiable: true },
      { name: 'Watch educational content', iconId: 'graduation', category: 'learning', colorId: 'purple', frequency: 'daily', targetCount: 20, unitId: 'minutes', isQuantifiable: true },
      
      // Creative
      { name: 'Write in journal', iconId: 'pen', category: 'creative', colorId: 'cyan', frequency: 'daily', targetCount: 15, unitId: 'minutes', isQuantifiable: true },
      { name: 'Practice instrument', iconId: 'music', category: 'creative', colorId: 'pink', frequency: 'daily', targetCount: 30, unitId: 'minutes', isQuantifiable: true },
      { name: 'Draw or sketch', iconId: 'art', category: 'creative', colorId: 'orange', frequency: 'daily', targetCount: 20, unitId: 'minutes', isQuantifiable: true },
      
      // Social
      { name: 'Call family/friends', iconId: 'call', category: 'social', colorId: 'pink', frequency: 'daily', targetCount: 1, unitId: 'calls', isQuantifiable: false },
      { name: 'Send appreciation message', iconId: 'message', category: 'social', colorId: 'gold', frequency: 'daily', targetCount: 1, unitId: 'messages', isQuantifiable: false },
      
      // Personal Care
      { name: 'Skincare routine', iconId: 'skincare', category: 'personal', colorId: 'orange', frequency: 'daily', targetCount: 1, unitId: 'routines', isQuantifiable: false },
      { name: 'Brush teeth twice', iconId: 'tooth', category: 'personal', colorId: 'green', frequency: 'daily', targetCount: 2, unitId: 'times', isQuantifiable: true },
      { name: 'Take vitamins', iconId: 'pill', category: 'personal', colorId: 'blue', frequency: 'daily', targetCount: 1, unitId: 'doses', isQuantifiable: false }
    ];

    // Filter out templates that already exist (prevent duplicates)
    const newTemplates = habitTemplates.filter(template => {
      const templateNameLower = template.name.toLowerCase().trim();
      const exists = existingTemplateNames.has(templateNameLower);
      if (exists) {
        console.log(`⏭️ Skipping duplicate: "${template.name}"`);
      }
      return !exists;
    });
    
    console.log(`📝 Found ${newTemplates.length} new templates to add (${habitTemplates.length - newTemplates.length} already exist)`);
    
    if (newTemplates.length === 0) {
      console.log('✅ All templates already exist, no action needed');
      return;
    }
    
    // Add only new templates (no deletion of existing ones)
    const addPromises = newTemplates.map(template => 
      addDoc(templatesRef, {
        ...template,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true
      })
    );
    
    await Promise.all(addPromises);
    console.log(`✅ Added ${newTemplates.length} new habit templates to Firebase`);
    
    // Clear cache
    habitCache.delete('getGlobalTemplates');
    
  } catch (error) {
    console.error('❌ Error initializing templates:', error);
    throw error;
  }
};

// Clean up duplicate templates (utility function)
export const cleanupDuplicateTemplates = async () => {
  try {
    console.log('🧹 Cleaning up duplicate templates...');
    
    const templatesRef = collection(db, 'apps', 'habit-tracker', 'templates');
    const existingTemplatesSnapshot = await getDocs(templatesRef);
    
    const templateMap = new Map(); // name -> {doc, data}
    const duplicateDocs = [];
    
    existingTemplatesSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.name) {
        const nameKey = data.name.toLowerCase().trim();
        
        if (templateMap.has(nameKey)) {
          // This is a duplicate - mark for deletion
          duplicateDocs.push({
            doc: doc,
            name: data.name,
            created: data.createdAt?.toDate?.() || new Date(0)
          });
          console.log(`🗑️ Found duplicate: "${data.name}"`);
        } else {
          // First occurrence - keep it
          templateMap.set(nameKey, { doc, data });
        }
      }
    });
    
    if (duplicateDocs.length === 0) {
      console.log('✅ No duplicate templates found');
      return { cleaned: 0, total: existingTemplatesSnapshot.size };
    }
    
    console.log(`🗑️ Deleting ${duplicateDocs.length} duplicate templates...`);
    
    // Delete duplicates
    const deletePromises = duplicateDocs.map(item => deleteDoc(item.doc.ref));
    await Promise.all(deletePromises);
    
    // Clear cache
    habitCache.delete('getGlobalTemplates');
    
    console.log(`✅ Cleaned up ${duplicateDocs.length} duplicate templates`);
    return { 
      cleaned: duplicateDocs.length, 
      total: existingTemplatesSnapshot.size,
      remaining: existingTemplatesSnapshot.size - duplicateDocs.length
    };
    
  } catch (error) {
    console.error('❌ Error cleaning up duplicates:', error);
    throw error;
  }
};

// Get global habit templates
export const getGlobalTemplates = async () => {
  const cacheKey = 'getGlobalTemplates';
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log('📋 Using cached templates');
    return cached;
  }

  try {
    const templatesRef = collection(db, 'apps', 'habit-tracker', 'templates');
    const querySnapshot = await getDocs(templatesRef);
    
    const templates = [];
    querySnapshot.forEach((doc) => {
      templates.push({ id: doc.id, ...doc.data() });
    });
    
    // Filter active templates and sort
    const activeTemplates = templates
      .filter(template => template.isActive !== false)
      .sort((a, b) => a.name.localeCompare(b.name));
    
    setCache(cacheKey, activeTemplates);
    console.log(`✅ Loaded ${activeTemplates.length} global templates`);
    return activeTemplates;
  } catch (error) {
    console.error('❌ Error fetching global templates:', error);
    // Return empty array if Firebase not set up
    return [];
  }
};

// ===============================
// USER HABIT MANAGEMENT
// ===============================

// Create a new habit
export const createHabit = async (userId, habitData) => {
  if (!userId) {
    console.warn('No userId provided to createHabit');
    return null;
  }

  try {
    console.log('🔄 Creating habit:', habitData);
    
    const habitsRef = collection(db, 'users', userId, 'habits');
    const habitDoc = await addDoc(habitsRef, {
      name: habitData.name,
      icon: habitData.icon || '✅',
      color: habitData.color || '#1E90FF',
      frequency: habitData.frequency || 'daily',
      description: habitData.description || '',
      category: habitData.category || 'other',
      targetCount: habitData.targetCount || 1,
      unit: habitData.unit || 'time',
      difficulty: habitData.difficulty || 1,
      isQuantifiable: habitData.isQuantifiable || false,
      reminderTime: habitData.reminderTime || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: true
    });
    
    // Clear habits cache
    const cacheKeys = Array.from(habitCache.keys()).filter(key => 
      key.includes(`getUserHabits_${userId}`)
    );
    cacheKeys.forEach(key => habitCache.delete(key));
    
    console.log('✅ Habit created successfully:', habitDoc.id);
    return habitDoc.id;
  } catch (error) {
    console.error('❌ Error creating habit:', error);
    throw error;
  }
};

// Get all habits for a user with caching and simplified query
export const getUserHabits = async (userId) => {
  if (!userId) {
    console.warn('No userId provided to getUserHabits');
    return [];
  }

  const cacheKey = getCacheKey('getUserHabits', userId);
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log('📋 Using cached habits');
    return cached;
  }

  try {
    console.log('🔄 Loading user habits from Firebase...');
    
    const habitsRef = collection(db, 'users', userId, 'habits');
    const querySnapshot = await getDocs(habitsRef);
    
    const allHabits = [];
    querySnapshot.forEach((doc) => {
      allHabits.push({ id: doc.id, ...doc.data() });
    });
    
    // Filter and sort client-side to avoid Firebase index requirements
    const activeHabits = allHabits
      .filter(habit => habit.isActive !== false) // Default to active if undefined
      .sort((a, b) => {
        // Sort by createdAt (newest first)
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
    
    setCache(cacheKey, activeHabits);
    console.log(`✅ Loaded ${activeHabits.length} active habits from Firebase (${allHabits.length} total)`);
    return activeHabits;
  } catch (error) {
    console.error('❌ Error fetching habits:', error);
    return [];
  }
};

// Update a habit
export const updateHabit = async (userId, habitId, updateData) => {
  if (!userId || !habitId) {
    console.warn('Missing userId or habitId in updateHabit');
    return;
  }

  try {
    console.log('🔄 Updating habit:', habitId, updateData);
    
    const habitRef = doc(db, 'users', userId, 'habits', habitId);
    await updateDoc(habitRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    
    // Clear habits cache
    const cacheKeys = Array.from(habitCache.keys()).filter(key => 
      key.includes(`getUserHabits_${userId}`)
    );
    cacheKeys.forEach(key => habitCache.delete(key));
    
    console.log('✅ Habit updated successfully:', habitId);
  } catch (error) {
    console.error('❌ Error updating habit:', error);
    throw error;
  }
};

// Delete a habit (HARD DELETE - completely removes from Firebase)
export const deleteHabit = async (userId, habitId) => {
  if (!userId || !habitId) {
    console.warn('Missing userId or habitId in deleteHabit');
    return;
  }

  try {
    console.log('🔄 Deleting habit permanently:', habitId);
    
    // Delete the main habit document
    const habitRef = doc(db, 'users', userId, 'habits', habitId);
    await deleteDoc(habitRef);
    
    // Delete all associated logs (optional - you might want to keep them for data integrity)
    // For now, we'll keep the logs for historical data
    
    // Clear habits cache
    const cacheKeys = Array.from(habitCache.keys()).filter(key => 
      key.includes(`getUserHabits_${userId}`) ||
      key.includes(`_${habitId}`)
    );
    cacheKeys.forEach(key => habitCache.delete(key));
    
    console.log('✅ Habit deleted permanently from Firebase:', habitId);
  } catch (error) {
    console.error('❌ Error deleting habit:', error);
    throw error;
  }
};

// Log habit completion
export const logHabitCompletion = async (userId, habitId, date = new Date(), completed = true, additionalData = {}) => {
  if (!userId || !habitId) {
    console.warn('Missing userId or habitId in logHabitCompletion');
    return null;
  }

  try {
    const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD format
    const logId = `${habitId}_${dateString}`;
    
    const logRef = doc(db, 'users', userId, 'habits', habitId, 'logs', logId);
    const logData = {
      completed,
      date: date,
      loggedAt: serverTimestamp(),
      ...additionalData // Include actualValue, targetValue, notes, completionPercentage
    };

    await setDoc(logRef, logData);
    
    // Clear related caches
    const cacheKeys = Array.from(habitCache.keys()).filter(key => 
      key.includes(`getHabitLogs_${userId}_${habitId}`) ||
      key.includes(`getTodayHabitStatus_${userId}_${habitId}`) ||
      key.includes(`getHabitAnalytics_${userId}_${habitId}`)
    );
    cacheKeys.forEach(key => habitCache.delete(key));
    
    console.log('Habit completion logged:', logId, logData);
    return logId;
  } catch (error) {
    console.error('Error logging habit completion:', error);
    throw error;
  }
};

// Get habit completion logs for a specific period with caching
export const getHabitLogs = async (userId, habitId, startDate, endDate) => {
  if (!userId || !habitId) {
    console.warn('Missing userId or habitId in getHabitLogs');
    return [];
  }

  const cacheKey = getCacheKey('getHabitLogs', userId, habitId, startDate.toISOString(), endDate.toISOString());
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log('📊 Using cached habit logs');
    return cached;
  }

  try {
    // Use simplified query without date range to avoid index requirements
    const logsRef = collection(db, 'users', userId, 'habits', habitId, 'logs');
    
    const querySnapshot = await getDocs(logsRef);
    const allLogs = [];
    querySnapshot.forEach((doc) => {
      allLogs.push({ id: doc.id, ...doc.data() });
    });
    
    // Filter client-side by date range
    const filteredLogs = allLogs
      .filter(log => {
        const logDate = log.date?.toDate?.() || new Date(log.date);
        return logDate >= startDate && logDate <= endDate;
      })
      .sort((a, b) => {
        const dateA = a.date?.toDate?.() || new Date(a.date);
        const dateB = b.date?.toDate?.() || new Date(b.date);
        return dateB - dateA; // Newest first
      });
    
    setCache(cacheKey, filteredLogs);
    console.log(`✅ Loaded ${filteredLogs.length} habit logs from Firebase`);
    return filteredLogs;
  } catch (error) {
    console.error('Error fetching habit logs:', error);
    // Return empty array instead of throwing
    return [];
  }
};

// Get today's habit status with caching
export const getTodayHabitStatus = async (userId, habitId) => {
  if (!userId || !habitId) {
    console.warn('Missing userId or habitId in getTodayHabitStatus');
    return { completed: false, date: new Date() };
  }

  const today = new Date();
  const cacheKey = getCacheKey('getTodayHabitStatus', userId, habitId, today.toDateString());
  const cached = getFromCache(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const dateString = today.toISOString().split('T')[0];
    const logId = `${habitId}_${dateString}`;
    
    const logRef = doc(db, 'users', userId, 'habits', habitId, 'logs', logId);
    const logDoc = await getDoc(logRef);
    
    let result;
    if (logDoc.exists()) {
      result = logDoc.data();
    } else {
      result = { completed: false, date: today };
    }
    
    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error fetching today\'s habit status:', error);
    // Return default status instead of throwing
    return { completed: false, date: today };
  }
};

// Get habit analytics with caching and optimized queries
export const getHabitAnalytics = async (userId, habitId, days = 30) => {
  if (!userId || !habitId) {
    console.warn('Missing userId or habitId in getHabitAnalytics');
    return {
      completionRate: 0,
      currentStreak: 0,
      totalCompleted: 0,
      totalDays: days,
      dailyData: []
    };
  }

  const cacheKey = getCacheKey('getHabitAnalytics', userId, habitId, days);
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log('📈 Using cached habit analytics');
    return cached;
  }

  try {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000);
    
    const logs = await getHabitLogs(userId, habitId, startDate, endDate);
    
    // Calculate completion rate
    const totalDays = days;
    const completedDays = logs.filter(log => log.completed).length;
    const completionRate = totalDays > 0 ? (completedDays / totalDays) * 100 : 0;
    
    // Calculate current streak
    let currentStreak = 0;
    const sortedLogs = logs.sort((a, b) => {
      const dateA = a.date?.toDate?.() || new Date(a.date);
      const dateB = b.date?.toDate?.() || new Date(b.date);
      return dateB - dateA; // Newest first
    });
    
    for (const log of sortedLogs) {
      if (log.completed) {
        currentStreak++;
      } else {
        break;
      }
    }
    
    // Generate daily completion data for chart
    const dailyData = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(endDate.getTime() - i * 24 * 60 * 60 * 1000);
      const dateString = date.toISOString().split('T')[0];
      const log = logs.find(l => {
        const logDate = l.date?.toDate?.() || new Date(l.date);
        return logDate.toISOString().split('T')[0] === dateString;
      });
      
      dailyData.push({
        date: dateString,
        completed: log ? (log.completed ? 1 : 0) : 0,
        day: date.toLocaleDateString('en-US', { weekday: 'short' })
      });
    }
    
    const result = {
      completionRate: Math.round(completionRate),
      currentStreak,
      totalCompleted: completedDays,
      totalDays,
      dailyData
    };
    
    setCache(cacheKey, result);
    console.log('✅ Calculated habit analytics');
    return result;
  } catch (error) {
    console.error('Error calculating habit analytics:', error);
    // Return default analytics instead of throwing
    return {
      completionRate: 0,
      currentStreak: 0,
      totalCompleted: 0,
      totalDays: days,
      dailyData: []
    };
  }
};

// Clear all habit caches (useful for debugging)
export const clearHabitCache = () => {
  habitCache.clear();
  console.log('🧹 Habit cache cleared');
}; 